import React, { useRef, useState } from 'react';
import { TextField } from '@material-ui/core';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import Typography from 'Components/Atoms/Typography';
import Modal from 'Components/Molecules/Modal';
import { useTranslation } from 'react-i18next';
import { creditPaymentRequest, PaymentRequest } from 'api/paymentRequestsApi';
import { euro } from './format';
import { useFieldIds } from './fieldIds';

/**
 * Manual confirmation that a deposit was deducted on the restaurant's own
 * final bill. Gastronaut has no POS integration, so the POS receipt reference
 * is recorded as the evidence. Credited is what was paid and not refunded
 * (a part not deducted is refunded first).
 */
const CreditModal: React.FC<{
  restaurantId: string;
  request: PaymentRequest;
  onClose: () => void;
  onDone: (request: PaymentRequest) => void;
}> = ({ restaurantId, request, onClose, onDone }) => {
  const { t } = useTranslation('reservations');
  const fieldId = useFieldIds('credit');
  const [posReference, setPosReference] = useState('');
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState('');

  const netCents = request.creditableCents;
  const valid = !!posReference.trim() && netCents > 0;

  const submit = async () => {
    if (!valid || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      onDone(await creditPaymentRequest(restaurantId, request.id, { posReference: posReference.trim(), amountCents: netCents }));
    } catch (err: any) {
      setError(err.message);
      setBusy(false);
      submitting.current = false;
    }
  };

  return (
    <Modal open title={t('Confirm credit on the final bill manually')} maxWidth="sm" fullWidth onClose={busy ? undefined : onClose}>
      <Box style={{ display: 'grid', gap: 12 }}>
        <Typography variant="text-3" block translation={null}>
          {t('Gastronaut does not create the final bill and is not connected to your POS. Confirm only once the deposit was deducted there.')}
        </Typography>
        <TextField
          id={fieldId('pos-reference')}
          size="small"
          variant="outlined"
          label={t('Receipt or invoice number of the final bill')}
          value={posReference}
          disabled={busy}
          onChange={(event) => setPosReference(event.target.value)}
        />
        <Typography variant="text-3" block translation={null}>
          {t('Credited: {{amount}}, the paid deposit minus refunds. Refund a part not deducted first.', {
            amount: euro(netCents),
          })}
        </Typography>
        {error && (
          <Typography color="error" block translation={null}>
            <span role="alert">{error}</span>
          </Typography>
        )}
        <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="outlined" translation={null} disabled={busy} onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button variant="primary" translation={null} loading={busy} disabled={busy || !valid} onClick={submit}>
            {t('Confirm credit')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreditModal;
